import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AdminSidebar from './AdminSidebar';
import AdminStatsCards from './AdminStatsCards';
import AdminTableView from './AdminTableView';
import AdminHeader from './AdminHeader';
import LoadingSpinner from '../ui/Spinner';
import { getAccessToken } from './adminUtils';
import './AdminDashboard.css';

/**
 * Admin Dashboard - Main container for admin functionality
 * Provides comprehensive management interface for all system data
 */
const AdminDashboard = () => {
  const [activeView, setActiveView] = useState('overview');
  const [selectedTable, setSelectedTable] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Available tables for management
  const availableTables = [
    { key: 'users', label: 'Users', icon: '👥', description: 'System users and accounts' },
    { key: 'user_profiles', label: 'User Profiles', icon: '👤', description: 'Detailed user information' },
    { key: 'products', label: 'Products', icon: '🏷️', description: 'Designer products and listings' },
    { key: 'fabrics', label: 'Fabrics', icon: '🧵', description: 'Fabric options and customizations' },
    { key: 'customization_queries', label: 'Customization Queries', icon: '✂️', description: 'User customization requests' },
    { key: 'received_orders', label: 'Orders', icon: '📦', description: 'Order management and tracking' },
    { key: 'bidding_orders', label: 'Bidding', icon: '💰', description: 'Bid management system' },
    { key: 'addresses', label: 'Addresses', icon: '📍', description: 'User shipping addresses' },
    { key: 'status', label: 'Order Status', icon: '📊', description: 'Order status tracking' },
    { key: 'template_configurations', label: 'Templates', icon: '🎨', description: 'Website template settings' },
    { key: 'designer_profiles', label: 'Designer Profiles', icon: '👗', description: 'Designer onboarding data' },
    { key: 'designer_payouts', label: 'Payouts', icon: '💳', description: 'Designer payment management' },
  ];

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const token = getAccessToken();

      if (!token) {
        setError('Admin authentication required');
        return;
      }

      const response = await fetch('/api/admin/dashboard-stats/', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 403) {
          setError('Admin access denied. You need admin privileges to access this dashboard.');
        } else {
          throw new Error('Failed to fetch dashboard statistics');
        }
        return;
      }

      const data = await response.json();
      setDashboardStats(data);
    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleTableSelect = (tableKey) => {
    setSelectedTable(tableKey);
    setActiveView('table');
  };

  const handleBackToOverview = () => {
    setActiveView('overview');
    setSelectedTable(null);
  };

  if (loading) {
    return (
      <div className="admin-dashboard admin-dashboard--loading">
        <LoadingSpinner size="large" />
        <p className="admin-dashboard__loading-text">Loading Admin Dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-dashboard admin-dashboard--error">
        <div className="admin-error">
          <div className="admin-error__icon">🚫</div>
          <h2 className="admin-error__title">Access Denied</h2>
          <p className="admin-error__message">{error}</p>
          <button
            className="admin-error__button"
            onClick={() => window.location.href = '/'}
          >
            Return to Homepage
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`admin-dashboard ${sidebarCollapsed ? 'admin-dashboard--sidebar-collapsed' : ''}`}>
      <AdminSidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        activeView={activeView}
        onViewChange={setActiveView}
        availableTables={availableTables}
        onTableSelect={handleTableSelect}
        selectedTable={selectedTable}
      />

      <div className="admin-dashboard__main">
        <AdminHeader
          activeView={activeView}
          selectedTable={selectedTable}
          onBackToOverview={handleBackToOverview}
          availableTables={availableTables}
        />

        <main className="admin-dashboard__content">
          <AnimatePresence mode="wait">
            {activeView === 'overview' && (
              <motion.div
                key="overview"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="admin-overview"
              >
                <div className="admin-overview__header">
                  <h1 className="admin-overview__title">
                    <span className="admin-overview__icon">⚡</span>
                    VASSTR Admin Dashboard
                  </h1>
                  <p className="admin-overview__subtitle">
                    Comprehensive system management and analytics
                  </p>
                </div>

                <AdminStatsCards stats={dashboardStats} />

                <div className="admin-overview__tables">
                  <h2 className="admin-overview__section-title">Data Management</h2>
                  <div className="admin-tables-grid">
                    {availableTables.map((table) => (
                      <motion.div
                        key={table.key}
                        className="admin-table-card"
                        whileHover={{ y: -4, scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleTableSelect(table.key)}
                      >
                        <div className="admin-table-card__icon">{table.icon}</div>
                        <h3 className="admin-table-card__title">{table.label}</h3>
                        <p className="admin-table-card__description">{table.description}</p>
                        <div className="admin-table-card__arrow">→</div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {activeView === 'table' && selectedTable && (
              <motion.div
                key={`table-${selectedTable}`}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="admin-table-view"
              >
                <AdminTableView
                  tableName={selectedTable}
                  tableConfig={availableTables.find(t => t.key === selectedTable)}
                  onBack={handleBackToOverview}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;